import React, { createContext, useContext, useState, useEffect } from 'react';
import { fetchBannerApi } from '../apis/banner';

interface Banner {
    id: string;
    title: string;
    countdownDate: Date;
    thumbnailOverlay?: string;
    isActive: boolean;
    createdAt?: Date;
    updatedAt?: Date;
}

interface BannerContextType {
    banner: Banner | null;
    loading: boolean;
    error: string | null;
}

const BannerContext = createContext<BannerContextType | undefined>(undefined);

export const BannerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [banner, setBanner] = useState<Banner | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const loadBanner = async () => {
            try {
                const apiBanner = await fetchBannerApi();
                if (apiBanner) {
                    setBanner({
                        id: apiBanner.id,
                        title: apiBanner.title,
                        countdownDate: new Date(apiBanner.countdownDate), // Convert ISO string to Date
                        thumbnailOverlay: apiBanner.thumbnailOverlay,
                        isActive: apiBanner.isActive,
                        createdAt: apiBanner.createdAt ? new Date(apiBanner.createdAt) : undefined,
                        updatedAt: apiBanner.updatedAt ? new Date(apiBanner.updatedAt) : undefined,
                    });
                } else {
                    setBanner(null);
                }
            } catch (err: any) {
                console.error('Error fetching banner:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        loadBanner();
    }, []);

    return (
        <BannerContext.Provider value={{ banner, loading, error }}>
            {children}
        </BannerContext.Provider>
    );
};

export const useBanner = () => {
    const context = useContext(BannerContext);
    if (context === undefined) {
        throw new Error('useBanner must be used within a BannerProvider');
    }
    return context;
};
